import { summarizeOrderFinancials } from './orderFinancials'
import { getPrice, getQuantity } from 'src/utils/priceFunctions'

export const ORDER_CHECKLIST_CSV_HEADERS = [
  'SKU',
  'Qty',
  'Size',
  'Stock',
  'Price',
  'UoM',
  'Notes',
]

/**
 * Same SKU rules as the checklist table:
 * - pending: product.sku only
 * - approved: order.sku, then product.sku
 * Empty strings / whitespace count as missing → "N/A" (use || not ??).
 */
export const getChecklistDisplaySku = (order, table = 'approved') => {
  const raw =
    table === 'approved'
      ? order?.sku || order?.product?.sku
      : order?.product?.sku
  const sku = typeof raw === 'string' ? raw.trim() : raw
  return sku || 'N/A'
}

/**
 * Map a checklist line (pending or approved) to one CSV row.
 * Same columns for both so pending and approved downloads stay aligned.
 * Qty/Price use shared helpers so supplier amendments are reflected.
 */
export const mapOrderToCsvRow = (order, table = 'approved') => [
  getChecklistDisplaySku(order, table),
  getQuantity(order),
  order?.size || order?.product?.size,
  order?.title || order?.product?.title,
  getPrice(order),
  order?.UoM || order?.product?.UoM,
  order?.productNotes ? order.productNotes : order?.product?.productNotes,
]

/**
 * Build the full CSV body array (line rows + total + account footer).
 * Does not mutate orders — safe for pending downloads that must not approve.
 */
export const buildOrderChecklistCsvData = ({
  pendingOrders = [],
  approvedOrders = [],
  pendingTotal = 0,
  approvedTotal = 0,
  accountNumber,
  orderDate,
  deliveryDate,
  poNumber,
  venueId,
  supplierId,
}) => {
  const data = [
    ...pendingOrders.map(order => mapOrderToCsvRow(order, 'noApproved')),
    ...approvedOrders.map(order => mapOrderToCsvRow(order, 'approved')),
  ]
  const submitted = summarizeOrderFinancials(
    [...pendingOrders, ...approvedOrders],
    {
      venueId,
      supplierId,
      purchaseOrderNumber: poNumber === 'all' ? undefined : poNumber,
    },
  )
  data.push([
    [
      submitted.hasFinancialSnapshots ? 'Goods total (ex VAT)' : 'Total',
      `£${(+approvedTotal + +pendingTotal).toFixed(2)}`,
    ],
  ])
  if (submitted.hasConflict)
    data.push([
      ['Submitted totals', 'Review required: conflicting saved amounts'],
    ])
  else
    for (const po of submitted.purchaseOrders) {
      data.push([
        ['PO Number', po.purchaseOrderNumber],
        [
          'Submitted goods (ex VAT)',
          `£${(po.orderFinancialSnapshot.goodsSubtotalPennies / 100).toFixed(
            2,
          )}`,
        ],
        [
          'Delivery (ex VAT)',
          `£${(
            po.orderFinancialSnapshot.appliedDeliveryFeePennies / 100
          ).toFixed(2)}`,
        ],
        [
          'Submitted order total (ex VAT)',
          `£${(po.orderFinancialSnapshot.orderTotalPennies / 100).toFixed(2)}`,
        ],
      ])
    }
  data.push([[]])
  data.push([
    ['Account number', accountNumber],
    ['Order Date', orderDate],
    ['Delivery Date', deliveryDate],
    ['PO Number', poNumber],
  ])
  return data
}
